import { config } from "../config";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const DEFAULT_TIMEOUT_MS = 30_000;

export interface ApiClientOptions {
  baseUrl: string;
  apiKey: string;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

/** Cliente HTTP minimo de la API: cabecera X-API-Key, timeout y errores con el mensaje del servidor. */
export function createApiClient({ baseUrl, apiKey, fetchImpl, timeoutMs = DEFAULT_TIMEOUT_MS }: ApiClientOptions) {
  async function get<T>(path: string, params: Record<string, string | number | undefined> = {}, signal?: AbortSignal): Promise<T> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") query.set(key, String(value));
    }
    const search = query.toString();
    const url = `${baseUrl}${path}${search ? `?${search}` : ""}`;

    // AbortController + setTimeout en lugar de AbortSignal.timeout/any: funciona en Safari < 17.4.
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    const onAbort = () => controller.abort();
    signal?.addEventListener("abort", onAbort, { once: true });

    let response: Response;
    try {
      // fetch se resuelve en cada llamada (no al importar) para respetar polyfills y mocks.
      response = await (fetchImpl ?? globalThis.fetch)(url, { headers: { Accept: "application/json", "X-API-Key": apiKey }, signal: controller.signal });
    } catch (error) {
      if (timedOut) throw new ApiError(408, "El servidor tardó demasiado en responder");
      throw error;
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
    }

    const body = (await response.json().catch(() => null)) as ({ message?: string } & T) | null;
    if (!response.ok) {
      throw new ApiError(response.status, body?.message || `Error ${response.status}`);
    }
    return body as T;
  }

  /** URL de una imagen pasando por el proxy del backend (evita hotlinking y bloqueos por Referer). */
  function imageUrl(original: string | null | undefined): string {
    if (!original) return "";
    return `${baseUrl}/api/v1/anime/image-proxy?url=${encodeURIComponent(original)}`;
  }

  return { get, imageUrl };
}

export type ApiClient = ReturnType<typeof createApiClient>;

export const api = createApiClient({ baseUrl: config.apiBaseUrl, apiKey: config.apiKey });
